import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = `Candidate|Exam|Section`;

export type SPCandidateExamSectionLoc =
	| 'SPCandidateExamSectionTitleSectionDetails'
	| 'SPCandidateExamSectionLabelOrdinalId'
	| 'SPCandidateExamSectionLabelName'
	| 'SPCandidateExamSectionLabelTitle'
	| 'SPCandidateExamSectionLabelGrade'
	| 'SPCandidateExamSectionLabelScored'
	| 'SPCandidateExamSectionLabelAlgorithm'
	| 'SPCandidateExamSectionLabelScore'
	| 'SPCandidateExamSectionLabelThetaScore'
	| 'SPCandidateExamSectionLabelMastery'
	| 'SPCandidateExamSectionLabelNumberCorrect'
	| 'SPCandidateExamSectionLabelNumberIncorrect'
	| 'SPCandidateExamSectionLabelNumberSkipped'
	| 'SPCandidateExamSectionContentOrdinalUnknown'
	| 'SPCandidateExamSectionTitleTab';

export const spCandidateExamSectionLocs: LocDefs<SPCandidateExamSectionLoc> = {
	SPCandidateExamSectionTitleSectionDetails: {
		defaultValue: 'Section Details',
		description: 'Title for the Section Details page.',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.Title.SectionDetails',
	},
	SPCandidateExamSectionLabelOrdinalId: {
		defaultValue: 'ID',
		description: 'Label for: Ordinal ID.',
		displayPath: `${dpRoot}|Ordinal ID Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.OrdinalId',
	},
	SPCandidateExamSectionLabelName: {
		defaultValue: 'Name',
		description: 'Label for: Name.',
		displayPath: `${dpRoot}|Name Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Name',
	},
	SPCandidateExamSectionLabelTitle: {
		defaultValue: 'Title',
		description: 'Label for: Title.',
		displayPath: `${dpRoot}|Title Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Title',
	},
	SPCandidateExamSectionLabelGrade: {
		defaultValue: 'Grade',
		description: 'Label for: Grade.',
		displayPath: `${dpRoot}|Grade Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Grade',
	},
	SPCandidateExamSectionLabelScored: {
		defaultValue: 'Scored',
		description: 'Label for: Scored.',
		displayPath: `${dpRoot}|Scored Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Scored',
	},
	SPCandidateExamSectionLabelAlgorithm: {
		defaultValue: 'Algorithm',
		description: 'Label for: Algorithm.',
		displayPath: `${dpRoot}|Algorithm Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Algorithm',
	},
	SPCandidateExamSectionLabelScore: {
		defaultValue: 'Score',
		description: 'Label for: Score.',
		displayPath: `${dpRoot}|Score Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Score',
	},
	SPCandidateExamSectionLabelThetaScore: {
		defaultValue: 'Theta Score',
		description: 'Label for: Theta Score.',
		displayPath: `${dpRoot}|Theta Score Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.ThetaScoreDisplayInUi,
	},
	SPCandidateExamSectionLabelMastery: {
		defaultValue: 'Mastery',
		description: 'Label for: Mastery.',
		displayPath: `${dpRoot}|Mastery Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.Mastery',
	},
	SPCandidateExamSectionLabelNumberCorrect: {
		defaultValue: 'Number Correct',
		description: 'Label for: Number Correct.',
		displayPath: `${dpRoot}|Number Correct Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.NumberCorrect',
	},
	SPCandidateExamSectionLabelNumberIncorrect: {
		defaultValue: 'Number Incorrect',
		description: 'Label for: Number Incorrect.',
		displayPath: `${dpRoot}|Number Incorrect Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.NumberIncorrect',
	},
	SPCandidateExamSectionLabelNumberSkipped: {
		defaultValue: 'Number Skipped',
		description: 'Label for: Number Skipped.',
		displayPath: `${dpRoot}|Number Skipped Label`,
		displayType: 'Label',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.ColumnHeading.NumberSkipped',
	},
	SPCandidateExamSectionContentOrdinalUnknown: {
		defaultValue: 'Unknown',
		description: 'Content for: Unknown ordinal.',
		displayPath: `${dpRoot}|Ordinal Unknown Content`,
		displayType: 'Content',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.Content.OrdinalUnknown',
	},
	SPCandidateExamSectionTitleTab: {
		defaultValue: 'Sections',
		description: 'Title for the Sections tab.',
		displayPath: `${dpRoot}|Sections Tab Title`,
		displayType: 'Title',
		portal: 'SP',
		feature: HubFeature.Exam,
		legacyName: 'Exam/CandidateExamSection.Title.Tab',
	},
};

export const LOC_KEYS = MapLocKeys<SPCandidateExamSectionLoc>(spCandidateExamSectionLocs);

export type SPCandidateExamSectionLocType = LocMap<SPCandidateExamSectionLoc>;
